import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import clsx from "clsx";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import { CData } from "src/Data";
import * as Dati from "src/Dati";
import * as Glob from "src/Globale";
import { Trad } from "src/components/";
import { _Autocomplete, _DateField, _NumberField, _Select } from "../../form";
import { _PolicyCARFieldsList, _CoInsurance, _CheckFileList } from "..";
import { IRouteParam } from "../../pages/Policy";

interface IPolicyFormContentProps {
    PolicyData: any;
    Data: Dati.CRisAPIDatoTipiInfo_Polizza;
    Limits: Dati.Dictionary<number>;
    ProductBinderJSON: string;
    RiduzioneSommaGarantita: number | undefined;
    OnBlurAmount: () => void;
    OnChangeAmount: (e: string, val: string) => void;
    OnChangeBondData: (e: string, val: unknown) => void;
    OnChangeDerogation: (e: React.FormEvent<any>, tipo: string, verso: string) => void;
    OnChange: (e: React.FormEvent<any> | string, val?: unknown) => void;
}

const useStyles = makeStyles(({ spacing }: Theme) => ({
    state: {
        marginTop: spacing(1)
    }
}));

export const _PolicyCARFormContent: React.FC<IPolicyFormContentProps> = ({
    PolicyData,
    Data,
    Limits,
    ProductBinderJSON,
    RiduzioneSommaGarantita,
    OnBlurAmount,
    OnChangeAmount,
    OnChangeBondData,
    OnChangeDerogation,
    OnChange
}) => {
    const classes = useStyles();
    const { ID_Anag, ID_Polizza } = useParams<IRouteParam>();
    const OnChangeProductType = (e: React.FormEvent<any> | string, val?: unknown) => {
        OnChange(e, val);
        OnChange("Dato.CoAssicurazioneJSON", "");
        OnChange("Info", {});
    };

    const SetDisabledDates = (): boolean[] => {
        let isStartDateDisabled = false;
        let isEndDateDisabled = false;

        for (var key in Data.Info) {
            if (Data.Info[key].Campo == "DataPresentazione") {
                isStartDateDisabled = true;
            }
            if (Data.Info[key].Campo == "DataSvincolo") {
                isEndDateDisabled = true;
            }
        }

        return [isStartDateDisabled, isEndDateDisabled];
    };

    const OnChangeDataEffetto = (name: string, val: string | Date) => {
        const startDate: Date = new CData(moment(val).toDate(), true).Data;
        const endDate: Date = new CData(startDate, true).AddAnni(1).Data;
        OnChange("Dato.DataFine", endDate);
        OnChangeBondData("DataFineLavori", endDate);
        OnChangeBondData("DataInizioLavori", val);
        OnChange(name, val);
    };

    const OnChangeDataFine = (name: string, val: string | Date) => {
        OnChangeBondData("DataFineLavori", val);
        OnChange(name, val);
    };

    const OnChangeDuration = (e: string, val: string | number) => {
        if (PolicyData.DataPresentazione != undefined && PolicyData.DataSvincolo != undefined) {
            const presentationDate: Date = moment(PolicyData.DataPresentazione).toDate();
            const releaseDate: Date = new CData(presentationDate, true).AddGiorni(Number(val)).Data;

            //OnChangeBondData("DataSvincolo", releaseDate);
            OnChange("Dato.DataFine", releaseDate);
            OnChange("Dato.DataEffetto", presentationDate);
        } else if (Data.Dato.DataEffetto != undefined && PolicyData.DataSvincolo != undefined) {
            const releaseDate: Date = new CData(moment(Data.Dato.DataEffetto).toDate(), true).AddGiorni(Number(val)).Data;

            //OnChangeBondData("DataSvincolo", releaseDate);
            OnChange("Dato.DataFine", releaseDate);
        }
        OnChangeBondData(e, val);
    };

    const [isDataEffettoDisabled, isDataFineDisabled] = useMemo(SetDisabledDates, [Data.Info]);
    
    return (
        <Grid item container spacing={4}>
            <Grid item xs={12} md={2}>
                <TextField fullWidth disabled id="Dato.ID_Polizza" label={Trad.Trad("ID")} value={Data.Dato.ID_Polizza} onChange={OnChange} />
            </Grid>
            <Grid item xs={12} md={4}>
                <_Autocomplete
                    Disabled
                    Value={Data.Dato.ID_AnagPOS || ""}
                    Name="Dato.ID_AnagPOS"
                    Label="POS"
                    OnChange={OnChange}
                    Url="/api/Anag/RicercaVeloce?id_TipoAnag=&ricerca="
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <_Autocomplete
                    Disabled
                    Value={Data.Dato.ID_AnagContraente || ""}
                    Name="Dato.ID_AnagContraente"
                    Label="Contraente"
                    OnChange={OnChange}
                    Url="/api/Anag/RicercaVeloce?id_TipoAnag=&ricerca="
                />
            </Grid>
            <Grid item xs={12} md={2}>
                {Data.Dato.StatoPolizza && (
                    <>
                        <InputLabel shrink>{Trad.Trad("Stato polizza")}</InputLabel>
                        <Chip
                            label={Trad.Trad(Data.Dato.StatoPolizza)}
                            size="small"
                            className={clsx(classes.state, {
                                warning: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_Preventivo,
                                info: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_PreventivoBozzaInviata,
                                success: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_Emessa,
                                error: Data.Dato.ID_TipoPolizzaStato == Glob.Tipi_PS_Rifiuto
                            })}
                        />
                    </>
                )}
            </Grid>
            <Grid item xs={12} md={2}>
                <TextField
                    fullWidth
                    disabled
                    id="Dato.Polizza"
                    label={Trad.Trad("Polizza")}
                    value={Data.Dato.Polizza || ""}
                    onChange={OnChange}
                    helperText={Glob.AzioneAbilitata("CodiceControlloVisualizza", Glob.Lettura) && Data.Dato.CodiceControllo}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <_Autocomplete
                    SearchInput
                    Value={Data.Dato.ID_TipoProdotto}
                    Name="Dato.ID_TipoProdotto"
                    Label="Prodotto"
                    Disabled={!!Number(ID_Polizza)}
                    OnChange={OnChangeProductType}
                    Url={`/api/Tipi/Tipo?tabella=TipiProdotti&campo=TipoProdotto`}
                />
            </Grid>
            <Grid item xs={12} md={2}>
                <_DateField Value={Data.Dato.DataEffetto} Name="Dato.DataEffetto" Label="Data Inizio Copertura" OnChange={OnChangeDataEffetto} Disabled={isDataEffettoDisabled} required={false} />
            </Grid>
            <Grid item xs={12} md={2}>
                <_DateField Value={Data.Dato.DataFine} Name="Dato.DataFine" Label="Data Termine Copertura" OnChange={OnChangeDataFine} Disabled={isDataFineDisabled} required={false} />
            </Grid>
            {ProductBinderJSON && (
                <Grid item xs={12}>
                    <_CoInsurance Name="Dato.CoAssicurazioneJSON" ValueJSON={Data.Dato.CoAssicurazioneJSON} ProductBinderJSON={ProductBinderJSON} OnChange={OnChange} />
                </Grid>
            )}
            <Grid item xs={12}>
                <_PolicyCARFieldsList
                    PolicyData={PolicyData}
                    Info={Data.Info}
                    Limits={Limits}
                    RiduzioneSommaGarantita={RiduzioneSommaGarantita}
                    OnBlurAmount={OnBlurAmount}
                    OnChangeAmount={OnChangeAmount}
                    OnChangeBondData={OnChangeBondData}
                    OnChangeDerogation={OnChangeDerogation}
                    OnChangeDuration={OnChangeDuration}
                />
            </Grid>
            {Data.Dato.ID_Polizza > 0 && (
                <Grid item xs={12}>
                    <_CheckFileList ValueJSON={Data.Dato.FileJSON} ID_TipoFileRichiesto={Data.Dato.ID_TipoFileRichiesto || ""} OnChange={OnChange} />
                </Grid>
            )}
        </Grid>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/PolicyCAR/PolicyCARFormContent.tsx